import type { PerformanceMeasure } from '@ms/yammer-telemetry';
import {
  endAndReportPerformanceMeasure,
  getErrorMessage,
  getErrorReason,
  startPerformanceMeasure,
} from '@ms/yammer-telemetry';
import type { PerformanceEventProperties } from '@ms/yammer-telemetry-support';

import { apiAction } from './apiAction';
import { getApiMeasureName } from './getApiMeasureName';
import type { ManagedApiCaching, ManagedApiExecutorResult } from './managedApiCachingExecutor';
import { executeManagedApiCaching } from './managedApiCachingExecutor';
import type { ManagedApiPerformanceMetadata, ManagedApiRequest } from './types';

export type { ManagedApiPerformanceMetadata } from './types';

export interface ManagedApiPerformanceReporter {
  readonly start: (apiMeasureName: string) => PerformanceMeasure;
  readonly end: (properties: PerformanceMeasure & { readonly eventProperties: PerformanceEventProperties }) => void;
}

export interface ManagedApiExecutorOptions<TResponse> {
  readonly request: ManagedApiRequest;
  readonly caching?: ManagedApiCaching;
  readonly performanceMetadata?: ManagedApiPerformanceMetadata;
  readonly executeRequest?: (request: ManagedApiRequest) => Promise<TResponse>;
  readonly responseWritten?: (response: TResponse, cacheKey: string) => void;
  readonly performanceReporter?: ManagedApiPerformanceReporter;
  readonly reportPerformance?: boolean;
}

const defaultPerformanceReporter: ManagedApiPerformanceReporter = {
  start: (apiMeasureName) => startPerformanceMeasure('managed_api_action', apiMeasureName),
  end: endAndReportPerformanceMeasure,
};

export const executeManagedApi = async <TResponse>({
  request,
  caching,
  performanceMetadata,
  executeRequest = (managedRequest) => apiAction<TResponse>(managedRequest, performanceMetadata),
  responseWritten,
  performanceReporter = defaultPerformanceReporter,
  reportPerformance = true,
}: ManagedApiExecutorOptions<TResponse>): Promise<ManagedApiExecutorResult<TResponse>> => {
  let cacheHit: boolean | undefined;
  let error: unknown;
  const apiMeasureName = getApiMeasureName(request);
  const measure = reportPerformance ? performanceReporter.start(apiMeasureName) : undefined;

  try {
    const result = await executeManagedApiCaching({ request, caching, executeRequest, responseWritten });
    cacheHit = result.cacheHit;

    return result;
  } catch (caughtError) {
    error = caughtError;
    throw caughtError;
  } finally {
    if (measure) {
      performanceReporter.end({
        ...measure,
        eventProperties: {
          ...measure.eventProperties,
          apiName: apiMeasureName,
          actionName: performanceMetadata?.actionName,
          actionType: performanceMetadata?.actionType,
          cacheExists: performanceMetadata?.cacheExists,
          cachingStrategy: caching?.strategy,
          endedWithError: error !== undefined,
          message: getErrorMessage(error),
          reason: getErrorReason(error),
          idempotencyToken: performanceMetadata?.idempotencyToken,
          requestId: '',
          initiator: request.initiator,
          cacheHit,
          ...performanceMetadata?.customProperties,
        },
      });
    }
  }
};
